import {
  EntitlementStatusType,
  HelpTypes,
  HelpUserTypes,
  IAdminEntitlement,
  PermissionsOfAdmin,
} from '@pharmaplan/common';

import editIcon from '../../../assets/svg/updateNoteIcon.svg';
import { DynamicTableCell } from '../../../helpers/Constants';
import { getDayMonthDateYear } from '../../Reports/helpers';
import strings from '../../../localization';
import { IUserProfileLinkSignature } from '../../../hooks/Admin/useUserProfileLink';

const { text, chip, customLink, bar } = DynamicTableCell;

export interface IEntitlementAutoCompleteOption {
  id: string;
  label: string;
  subtitle: string;
}

const statusStyleMap: Record<string, { color: string }> = {
  [EntitlementStatusType.Active]: {
    color: 'primary.main',
  },
  [EntitlementStatusType.Exhausted]: {
    color: 'warning.main',
  },
  [EntitlementStatusType.Expired]: {
    color: 'error.main',
  },
};

const statusLabelMap: Record<string, string> = {
  [EntitlementStatusType.Active]: strings.active,
  [EntitlementStatusType.Exhausted]: strings.exhausted,
  [EntitlementStatusType.Expired]: strings.expired,
};

const safeDate = (date?: string | null) => {
  if (!date) {
    return '-';
  }

  return getDayMonthDateYear(date);
};

interface IEntitlementRowsParams {
  can:(permission:PermissionsOfAdmin) => boolean;
  data: Array<IAdminEntitlement>;
  handleEdit: (id: string) => () => void;
  goToUserProfile: IUserProfileLinkSignature;
}

export const entitlementHeaders = () =>
  [
    {
      key: '0',
      label: strings.name,
      sortingKey: 'pharmacyName',
    },
    {
      key: '2',
      label: strings.campaignName,
      sortingKey: 'campaignName',
    },
    {
      key: '3',
      label: strings.totalCredits,
      sortingKey: 'totalCredits',
    },
    {
      key: '4',
      label: strings.creditsUsed,
      sortingKey: 'creditsUsed',
    },
    {
      key: '5',
      label: strings.creditsValidUntil,
      sortingKey: 'validUntil',
    },
    {
      key: '6',
      label: strings.status,
      sortingKey: 'status',
    },
    {
      key: '7',
      label: strings.action,
    },
  ];

export const entitlementRows = ({
  can,
  data,
  handleEdit,
  goToUserProfile,
}: IEntitlementRowsParams) =>
  data?.map((item) => {
    const {
      entitlementId,
      pharmacyName,
      campaignName,
      totalCredits,
      creditsUsed,
      pharmacyId,
      validUntil,
      status,
      userType,
    } = item;

    const canAllocateCredits = can(PermissionsOfAdmin.AdditionalCreditAllocation);

    return {
      key: entitlementId,
      data: [
        {
          key: '0',
          value: {
            label: pharmacyName,
            handlePress: goToUserProfile({
              personnelId: pharmacyId,
              userType:
                HelpUserTypes[
                  (userType ?? HelpTypes.pharmacy) as keyof typeof HelpUserTypes
                ],
            }),
          },
          type: customLink,
          maxWidth: 220,
        },
        {
          key: '2',
          value: campaignName,
          type: text,
          maxWidth: 220,
        },
        {
          key: '3',
          value: totalCredits,
          type: text,
        },
        {
          key: '4',
          value: creditsUsed,
          type: text,
        },
        {
          key: '5',
          value: safeDate(validUntil),
          type: text,
        },
        {
          key: '6',
          value: {
            label: statusLabelMap[status] || status,
            style:
              statusStyleMap[status]
              || statusStyleMap[EntitlementStatusType.Active],
          },
          type: chip,
        },
        {
          key: '7',
          value: [
            canAllocateCredits && {
              key: '1',
              icon: editIcon,
              tooltip: strings.edit,
              onClick: handleEdit(entitlementId),
            },
          ].filter(Boolean),
          type: bar,
        },
      ],
    };
  });
